import { useState } from 'react';
import classNames from 'classnames';

import lang from '../../../../../lib/language';
import { PurchasePaymentTypeEnum } from '../../../../../lib/models/crm/types';

import InputErrorIcon from '../../../../../assets/img/input-error-icon.svg';
import { ClickableSpan } from '../../../../../components';
import DiscoverButton from '../../../../../shared_components/DiscoverButton/DiscoverButton';
import { getCouponErrorMessage } from '../../paymentMethodSelection.helpers';

import styles from './CouponCode.module.scss';

type Props = {
  hasAppliedCoupon: boolean;
  isApplyingCoupon: boolean;
  couponError: string | null;
  purchaseType: PurchasePaymentTypeEnum;
  onApplyCoupon: (couponCode: string) => void;
  onRemoveCoupon: () => void;
  onClearCouponError?: () => void;
};

const CouponCode = ({
  hasAppliedCoupon,
  isApplyingCoupon,
  couponError,
  purchaseType,
  onApplyCoupon,
  onRemoveCoupon,
  onClearCouponError,
}: Props): JSX.Element => {
  const [isCouponInputOpen, setIsCouponInputOpen] = useState<boolean>(false);
  const [couponCode, setCouponCode] = useState<string>('');

  const couponErrorMessage = couponError ? getCouponErrorMessage(couponError, purchaseType) : null;

  const handleHaveCouponClick = () => {
    setIsCouponInputOpen(true);
  };

  const handleApply = () => {
    const trimmedCode = couponCode.trim();
    if (!trimmedCode || isApplyingCoupon) {
      return;
    }
    onApplyCoupon?.(trimmedCode);
  };

  const handleChange = (value: string) => {
    setCouponCode(value);
    if (!value.trim() && couponError) {
      onClearCouponError?.();
    }
  };

  const handleRemove = () => {
    setIsCouponInputOpen(false);
    setCouponCode('');
    onRemoveCoupon?.();
  };

  if (hasAppliedCoupon) {
    return (
      <div className={styles.appliedCouponWrapper}>
        <input
          type="text"
          className={styles.couponInput}
          value={couponCode}
          disabled
          readOnly
        />
        <div className={styles.couponAppliedRow}>
          <div className={styles.couponAppliedBadge}>
            {lang.paymentMethodSelectedPlanCouponApplied(couponCode)}
          </div>
          <ClickableSpan className={classNames(styles.couponLink, styles.removeButton)} onClick={handleRemove}>
            {lang.paymentMethodSelectedPlanRemoveCoupon()}
          </ClickableSpan>
        </div>
      </div>
    );
  }

  if (isCouponInputOpen || couponErrorMessage) {
    return (
      <div className={styles.couponInputWrapper}>
        <div className={styles.couponInputRow}>
          <input
            type="text"
            className={classNames(styles.couponInput, {
              [styles.couponInputError]: !!couponErrorMessage,
            })}
            value={couponCode}
            placeholder={lang.paymentMethodSelectedPlanCouponPlaceholder()}
            onChange={(event) => handleChange(event.target.value)}
          />
          <DiscoverButton
            text={lang.paymentMethodSelectedPlanApply()}
            className={styles.applyButton}
            disabled={!couponCode.trim() || isApplyingCoupon}
            onClick={handleApply}
          />
        </div>
        {couponErrorMessage && (
          <div className={styles.couponError}>
            <img src={InputErrorIcon} alt="" className={styles.couponErrorIcon} />
            {couponErrorMessage}
          </div>
        )}
      </div>
    );
  }

  return (
    <ClickableSpan className={styles.couponLink} onClick={handleHaveCouponClick}>
      {lang.paymentMethodSelectedPlanHaveCouponCode()}
    </ClickableSpan>
  );
};

export default CouponCode;
